import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, Lightbulb, X, Sparkles, CheckCircle2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { queryClient } from "@/lib/queryClient";

interface ChatMessage {
  role: "user" | "assistant" | "action";
  content: string;
}

const PAGE_CONTEXT: Record<string, { label: string; suggestions: string[] }> = {
  "/admin": {
    label: "Dashboard",
    suggestions: [
      "What needs my attention today?",
      "Summarize my business performance this month",
    ],
  },
  "/admin/reservations": {
    label: "Reservations",
    suggestions: [
      "Any booking conflicts this week?",
      "Which apartments have the most bookings?",
    ],
  },
  "/admin/apartments": {
    label: "Apartments",
    suggestions: [
      "Which units are underperforming?",
      "Rank my apartments by revenue",
    ],
  },
  "/admin/cleaning-jobs": {
    label: "Cleaning",
    suggestions: [
      "Are we on track for today's cleanings?",
      "Who's my most efficient cleaner?",
    ],
  },
  "/admin/finances": {
    label: "Finances",
    suggestions: [
      "Explain this month's revenue breakdown",
      "How much is unpaid right now?",
    ],
  },
  "/admin/reports": {
    label: "Reports",
    suggestions: [
      "What are the key trends in my data?",
      "Generate a summary for this period",
    ],
  },
  "/admin/users": {
    label: "Team",
    suggestions: [
      "Analyze my staffing levels",
      "Am I overstaffed or understaffed?",
    ],
  },
  "/admin/maintenance": {
    label: "Maintenance",
    suggestions: [
      "Any recurring maintenance issues?",
      "Which units need the most maintenance?",
    ],
  },
};

const DEFAULT_SUGGESTIONS = [
  "Give me a full business overview",
  "What's the biggest risk right now?",
];

function getPageContext(path: string) {
  for (const [key, val] of Object.entries(PAGE_CONTEXT)) {
    if (path === key || (key !== "/admin" && path.startsWith(key))) {
      return val;
    }
  }
  return { label: "General", suggestions: DEFAULT_SUGGESTIONS };
}

export default function LumiGlobal() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [location] = useLocation();

  const pageCtx = getPageContext(location);
  const isHidden = location === "/admin/lumi" || location === "/admin/forecast";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = useCallback(async (messageText?: string) => {
    const text = (messageText || input).trim();
    if (!text || isStreaming) return;

    const userMessage: ChatMessage = { role: "user", content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsStreaming(true);

    try {
      const response = await fetch("/api/admin/lumi/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: `[Context: User is on the ${pageCtx.label} page] ${text}`,
          history: newMessages.slice(-8),
        }),
      });

      if (!response.ok) throw new Error("Failed");

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No stream");

      const decoder = new TextDecoder();
      let assistantContent = "";
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.action) {
                setMessages(prev => {
                  const lastIsEmpty = prev.length > 0 && prev[prev.length - 1].role === "assistant" && !prev[prev.length - 1].content;
                  const base = lastIsEmpty ? prev.slice(0, -1) : prev;
                  return [...base, { role: "action" as const, content: data.action }, { role: "assistant", content: assistantContent }];
                });
                queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-jobs"] });
                queryClient.invalidateQueries({ queryKey: ["/api/maintenance"] });
                queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
                queryClient.invalidateQueries({ queryKey: ["/api/admin/finances"] });
                queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
                queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
              }
              if (data.content) {
                assistantContent += data.content;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: "assistant", content: assistantContent };
                  return updated;
                });
              }
              if (data.done) break;
            } catch {}
          }
        }
      }
    } catch {
      setMessages(prev => [
        ...prev.filter(m => m.role !== "assistant" || m.content !== ""),
        { role: "assistant", content: "Something went wrong. Try again." },
      ]);
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, messages, pageCtx.label]);

  // Listen for the sidebar button to open the panel
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("open-lumi", handler);
    return () => window.removeEventListener("open-lumi", handler);
  }, []);

  if (isHidden) return null;

  return (
    <>
      {isOpen && (
        <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[380px] max-h-[85vh] sm:max-h-[520px] bg-card border sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="lumi-global-panel">
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gradient-to-r from-amber-50/60 to-orange-50/30 dark:from-amber-950/10 dark:to-orange-950/5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Lightbulb className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <span className="text-sm font-bold">Lumi</span>
                <span className="text-[10px] text-muted-foreground ml-1.5">{pageCtx.label}</span>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsOpen(false)} data-testid="btn-close-lumi-global">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0" style={{ maxHeight: "360px" }}>
            {messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground text-center py-2">Ask anything about your {pageCtx.label.toLowerCase()} data</p>
                {pageCtx.suggestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    className="w-full text-left px-3 py-2 rounded-lg border bg-card hover:bg-accent transition-all text-xs text-muted-foreground hover:text-foreground flex items-center gap-2"
                    data-testid={`lumi-global-suggestion-${i}`}
                  >
                    <Sparkles className="w-3 h-3 text-amber-500 shrink-0" />
                    {q}
                  </button>
                ))}
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "action" ? (
                  <div className="flex items-start gap-1.5 max-w-[85%] rounded-lg px-2.5 py-1.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-emerald-800 dark:text-emerald-300">{msg.content}</p>
                  </div>
                ) : (
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    {msg.role === "assistant" ? (
                      <div className="prose prose-xs dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:text-xs [&_li]:text-xs [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_table]:text-[10px]">
                        <ReactMarkdown>{msg.content || "..."}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-xs">{msg.content}</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {isStreaming && messages[messages.length - 1]?.role !== "assistant" && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-xl px-3 py-2">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Thinking...
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t px-3 py-2">
            <div className="flex items-end gap-1.5">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Ask Lumi..."
                className="min-h-[36px] max-h-[72px] resize-none rounded-lg text-xs"
                rows={1}
                disabled={isStreaming}
                data-testid="input-lumi-global"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={!input.trim() || isStreaming}
                size="icon"
                className="h-9 w-9 rounded-lg shrink-0 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                data-testid="btn-send-lumi-global"
              >
                {isStreaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
